<template>
  <div>
    <v-row noGutters>
      <BaseSidebar
        :items="[]"
        :initialMini="true"
      />
    </v-row>
    <v-skeleton-loader
      v-if="isInitializing"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <TableRemote
      v-else-if="hasPermission('sites.view')"
      ref="tableRemoteRef"
      :headers="tableHeaders"
      :items="displayedItems"
      :pagination="pagination"
      :isSearchable="false"
      :sortableColumns="['siteName', 'siteNumber', 'client', 'clientNumber']"
      :title="$t('sites.pageName')"
      :loading="loading"
      isSelectable
      :isCustomIcon="true"
      :itemActions="actions"
      hideDefaultFooter
      @send-item="onClickRedirect"
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #SearchWithFilters>
        <SearchWithFilters
          :withoutFilter="true"
          :searchPlaceholder="$t('laboratory.placeholder.labWoSearchBy')"
          :filterName="filterName"
          @onSearch="handleSearch"
        />
        <v-switch
          v-model="queryParams.includeInactive"
          :label="$t('clients.labels.switchActiveInactive')"
        />
      </template>
      <template v-slot:item.siteName="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.siteName }}
        </div>
        <div v-else>
          {{ item.siteName }}
        </div>
      </template>

      <template v-slot:item.siteNumber="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.siteNumber }}
        </div>
        <div v-else>
          {{ item.siteNumber }}
        </div>
      </template>

      <template v-slot:item.site="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.site }}
        </div>
        <div v-else>
          {{ item.site }}
        </div>
      </template>

      <template v-slot:item.number="{item}">
        <div
          v-if="item.active===false"
          class="inactive"
        >
          {{ item.number }}
        </div>
        <div v-else>
          {{ item.number }}
        </div>
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <NoPermissionAlert
      v-else
      :message="$t('permissions.error')"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import BaseSidebar from '@/components/shared/navigation/BaseSidebar.vue';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import TransferSiteClientsForm from '@/views/sites/TransferSiteClientsForm.vue';
import SearchWithFilters from '@/components/shared/SearchWithFilters.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import DeleteForm from '@/components/shared/widgets/DeleteForm.vue';


export default {
    components: {
        TableRemote,
        BaseSidebar,
        NoPermissionAlert,
        SearchWithFilters,

    },
    data() {
        return {
            loading: false,
            isInitializing: false,
            items: [],
            pagination: {},
            filterName: 'allSites',
            queryParams: { perpage: 25, page: 1, includeInactive: false },
            isFetching: false,
        };
    },

    computed: {
        ...mapGetters([
            'hasPermission',
        ]),
        tableHeaders() {
            return [
                'siteName',
                'siteNumber',
                'client',
                'clientNumber',
            ].map(header => ({
                text: this.$t(`sites.labels.${header}`),
                value: header,
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('sites.transfer')) {
                actions.push({
                    name: this.$t('forms.btn.transferSite'),
                    icon: 'open_in_new',
                    mode: 'open_in_new',
                });
            }

            actions.push({
                name: this.$t('sites.active'),
                icon: 'edit',
                mode: 'edit',
                customValidationProperty: 'active',
                showOncustomValidationProperty: false,
            });
            actions.push({
                name: this.$t('sites.desactive'),
                icon: 'edit',
                mode: 'edit',
                customValidationProperty: 'active',
                showOncustomValidationProperty: true,
            });

            if (this.hasPermission('sites.remove')) {
                actions.push({
                    name: this.$t('forms.labels.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }
            return actions;
        },
        displayedItems() {
            return this.items.length ? this.items.map(({ client, ...item }) => ({
                siteName: item.name,
                siteNumber: item.deliveryAccountNumber,
                client: client.name,
                clientId: client.id,
                clientNumber: client.accountNumber,
                active: item.active,
                ...item,
            })) : [];
        },
    },

    watch: {
        'queryParams.includeInactive': {
            handler() {
                this.getItems(1);
            },
            deep: true,
        },
    },

    async created() {
        this.isInitializing = true;
        await this.getItems();
        this.isInitializing = false;
    },
    methods: {
        async getItems(nextPage = 1) {
            if (this.isFetching) return;
            this.loading = true;
            this.isFetching = true;
            // Initialisation des éléments pour la première page
            if (nextPage === 1 && this.queryParams.page === 1) {
                const { data } = await this.$rq.getSites(this.queryParams);
                this.pagination = data.pagination;
                this.items = [...data.sites];
            }
            // Mise à jour de la page et récupération des éléments supplémentaires
            if (nextPage <= this.queryParams.page) {
                this.loading = false;
                this.isFetching = false;
                return;
            }
            this.queryParams.page = nextPage;
            if (this.queryParams.page !== this.pagination.currentPage) {
                const { data } = await this.$rq.getSites(this.queryParams);
                this.items = [...this.items, ...data.sites];
                this.pagination = data.pagination;
            }
            this.loading = false;
            this.isFetching = false;
        },
        async fetchQuery({ order, ...params }) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
                order: this.prepareOrderParam(order),
            };
            await this.getItems();
        },
        prepareOrderParam(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;

            switch (name) {
                case 'sitename':
                    newValue = 'name';
                    break;
                case 'sitenumber':
                    newValue = 'delivery_account_number';
                    break;
                case 'client':
                    newValue = 'client.name';
                    break;
                case 'clientnumber':
                    newValue = 'client.account_number';
                    break;
                default:
                    return null;
            }

            return `${newValue}_${order}`;
        },
        onClickRedirect(item) {
            this.$router.push({
                name: 'siteTree',
                params: {
                    clientId: item.clientId,
                    siteId: item.id,
                },
                query: this.queryParams.includeInactive ? { includeInactive: this.queryParams.includeInactive } : {},
            });
        },
        onClickAction(item, action) {
            if (action.mode === 'open_in_new') {
                this.$root.$emit('showModal', {
                    title: this.$t('sites.transfer.title', { siteName: item.name }),
                    component: Vue.component('TransferSiteClientsForm', TransferSiteClientsForm),
                    onConfirm: requestPayload => this.transferSite(requestPayload, item),
                });
            }
            if (action.mode === 'edit') {
                const siteStatus = !item.active ? 'activate' : 'deactivate';
                this.$root.$emit('showModal', {
                    title: this.$t(`sites.${siteStatus}`),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t(`sites.${siteStatus}Msg`) },
                    onConfirm: () => this.updateActive(item),
                    isForm: false,
                });
            }
            if (action.mode === 'delete') {
                this.removeSite(item);
            }
        },

        async removeSite(item) {
            this.$root.$emit('showModal', {
                title: this.$t('sites.removeSite').toUpperCase(),
                component: Vue.component('DeleteForm', DeleteForm),
                buttonTitle: 'delete',
                payload: { msg: this.$t('sites.msg.remove'), security: this.$t('sites.msg.security') },
                isForm: true,
                onConfirm: async () => {
                    await this.$rq.removeSite(item.id);
                    this.$toasted.global.toast_success({
                        message: this.$t('sites.msg.siteDeleted', { data: item.deliveryAccountNumber }),
                    });
                    await this.getItems(1);
                },
            });
        },
        async transferSite(requestPayload, item) {
            const { clientId } = requestPayload;
            await this.$rq.transferSiteToAnotherClient(item.id, clientId);
            this.$toasted.global.toast_success({
                message: this.$t('sites.transfer.msg.trasferred', { siteName: item.siteName }),
            });
            await this.getItems(1);
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;

            if (isIntersecting && !this.isFetching && condition) {
                /* We pass +1 in argument to add new elements */
                await this.getItems(this.queryParams.page + 1);
                // access "v-autocomplete" ref
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                // workaround to refresh list after adding new items.
                    ref.onScroll();
                }
            }
        },
        handleSearch(search) {
            this.queryParams = {
                ...this.queryParams,
                search,
                page: 0,
            };
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined(this.queryParams, true);
            this.queryParams = cleanQueryParams;
            this.getItems(1);
        },
        async updateActive(item) {
            await this.$rq.updateSiteStatus(item.id, { status: !item.active });
            await this.getItems(1);
        },
    },
};
</script>
